import {Controller} from "@hotwired/stimulus"
import {Turbo} from "@hotwired/turbo-rails"

type SlideshowImage = {
    src: string
    title: string
    photographer: string
    description: string
    user: string
    imageId: string
}

export default class extends Controller {
    static targets = ["image", "title", "counter", "photographer", "description", "user", "likeButton"]
    static values = {
        locale: String
    }

    declare readonly imageTarget: HTMLImageElement
    declare readonly titleTarget: HTMLElement
    declare readonly counterTarget: HTMLElement
    declare readonly photographerTarget: HTMLElement
    declare readonly descriptionTarget: HTMLElement
    declare readonly userTarget: HTMLElement
    declare readonly likeButtonTarget: HTMLElement
    declare readonly hasLikeButtonTarget: boolean
    declare readonly localeValue: string

    private images: SlideshowImage[] = []
    private index = 0

    private clearHandler = this.clear.bind(this)

    connect() {
        this.element.addEventListener("close", this.clearHandler)
    }

    disconnect() {
        this.element.removeEventListener("close", this.clearHandler)
    }

    open(event: CustomEvent) {
        const {images, index} = event.detail || {}
        if (!Array.isArray(images) || images.length === 0) return

        this.images = images
        this.index = this.clampIndex(Number(index) || 0)
        this.render()
        ;(this.element as HTMLDialogElement).showModal()
    }

    close() {
        ;(this.element as HTMLDialogElement).close()
    }

    private clear() {
        this.imageTarget.src = ""
        this.imageTarget.alt = ""
        this.imageTarget.classList.add("opacity-0")
        this.titleTarget.textContent = ""
        this.photographerTarget.textContent = ""
        this.userTarget.textContent = ""
        this.descriptionTarget.textContent = ""
        this.counterTarget.textContent = ""
        if (this.hasLikeButtonTarget) {
            this.likeButtonTarget.innerHTML = ""
        }
        this.images = []
        this.index = 0
    }

    next() {
        if (this.images.length === 0) return
        this.index = (this.index + 1) % this.images.length
        this.render()
    }

    prev() {
        if (this.images.length === 0) return
        this.index = (this.index - 1 + this.images.length) % this.images.length
        this.render()
    }

    handleKey(event: KeyboardEvent) {
        const dialog = this.element as HTMLDialogElement
        if (!dialog.open) return

        if (event.key === "ArrowRight") this.next()
        if (event.key === "ArrowLeft") this.prev()
        if (event.key === "Escape") this.close()
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            this.imageTarget.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`)
            })
        } else {
            document.exitFullscreen()
        }
    }

    private render() {
        const image = this.images[this.index]

        // 1. Metadata update
        this.imageTarget.alt = image.title || ""
        this.titleTarget.textContent = image.title || ""
        this.photographerTarget.textContent = image.photographer ? "© " + image.photographer : ""
        this.userTarget.textContent = image.user ? "(" + image.user + ")" : ""
        this.descriptionTarget.textContent = image.description || ""
        this.counterTarget.textContent = `${this.index + 1} / ${this.images.length}`

        // Update like button
        this.updateLikeButton(image.imageId)

        // 2. Hide current image immediately (no transition for hiding to avoid seeing old image)
        this.imageTarget.style.transition = "none"
        this.imageTarget.classList.add("opacity-0")

        // 3. Define the fade-in helper
        let alreadyTriggered = false
        const triggerFadeIn = () => {
            if (alreadyTriggered) return
            alreadyTriggered = true

            this.imageTarget.src = image.src
            // Force a reflow to ensure the browser realizes the image is now opacity-0 and transition is none
            void this.imageTarget.offsetWidth
            // Re-enable transition and remove opacity-0 in the next frames
            requestAnimationFrame(() => {
                this.imageTarget.style.transition = ""
                requestAnimationFrame(() => {
                    this.imageTarget.classList.remove("opacity-0")
                })
            })
        }

        // 4. Wait for the new image to be ready
        const loader = new Image()
        loader.onload = triggerFadeIn
        loader.src = image.src
        if (loader.complete) triggerFadeIn()
    }

    private clampIndex(index: number) {
        if (this.images.length === 0) return 0
        return Math.min(Math.max(index, 0), this.images.length - 1)
    }

    private updateLikeButton(imageId: string) {

        if (!imageId) {
            if (this.hasLikeButtonTarget) {
                this.likeButtonTarget.innerHTML = ""
            }
            return
        }

        const locale = this.localeValue || "de"
        const url = `/${locale}/images/${imageId}/like_button`
        fetch(url, {
            headers: {
                "Accept": "text/vnd.turbo-stream.html"
            }
        })
            .then(response => {
                if (!response.ok) throw new Error("Network response was not ok")
                return response.text()
            })
            .then(html => {
                if (html.trim().length > 0) {
                    (Turbo as any).renderStreamMessage(html)
                }
            })
            .catch(error => {
                console.error("Error fetching like button:", error)
            })
    }
}
